import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, FileSpreadsheet, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { importApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ACCEPTED_TYPES = '.csv,.xlsx,.xls';

export function CSVImport({ onImportComplete }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [weekStartDate, setWeekStartDate] = useState('');
  const { toast } = useToast();

  const isValidFile = (file) => {
    const name = file.name.toLowerCase();
    return name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls');
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && isValidFile(file)) {
      selectFile(file);
    } else {
      toast({ title: "Invalid file", description: "Please upload a CSV or Excel (.xlsx) file", variant: "destructive" });
    }
  }, [toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) selectFile(file);
  };

  const selectFile = async (file) => {
    setSelectedFile(file);
    setPreview(null);
    setIsPreviewing(true);
    try {
      const result = await importApi.preview(file);
      setPreview(result);
      if (result.uses_relative_dates && !weekStartDate) {
        // Default to next Monday
        const today = new Date();
        const daysUntilMon = (8 - today.getDay()) % 7 || 7;
        const nextMon = new Date(today);
        nextMon.setDate(today.getDate() + daysUntilMon);
        setWeekStartDate(nextMon.toISOString().split('T')[0]);
      }
    } catch (error) {
      toast({ title: "Preview failed", description: error.response?.data?.detail || "Could not read file", variant: "destructive" });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const result = await importApi.spreadsheet(selectedFile, preview?.uses_relative_dates ? weekStartDate : null);
      toast({ title: "Import successful!", description: result.message });
      setSelectedFile(null);
      setPreview(null);
      setWeekStartDate('');
      if (onImportComplete) onImportComplete();
    } catch (error) {
      toast({ title: "Import failed", description: error.response?.data?.detail || "Failed to import", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setWeekStartDate('');
  };

  const isExcel = selectedFile?.name?.match(/\.xlsx?$/i);

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6" data-testid="csv-import-section">
      <h3 className="font-bold text-lg text-[#1e293b] mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        Import Posts
      </h3>
      
      {!selectedFile ? (
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            isDragOver ? 'border-[#2563eb] bg-[#eff6ff]' : 'border-[#e2e8f0] hover:border-[#2563eb]/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="csv-drop-zone"
        >
          <Upload className="w-10 h-10 text-[#cbd5e1] mx-auto mb-4" />
          <p className="text-[#475569] mb-2">Drag and drop your file here</p>
          <p className="text-xs text-[#94a3b8] mb-4">or</p>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#e2e8f0] text-sm font-medium text-[#475569] hover:border-[#2563eb] hover:text-[#2563eb] transition-colors">
              <FileSpreadsheet className="w-4 h-4" />
              Browse Files
            </span>
            <input type="file" accept={ACCEPTED_TYPES} onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
          </label>
          <p className="text-xs text-[#94a3b8] mt-4">
            Supports CSV and Excel (.xlsx). Auto-detects columns like Theme/Topic, Caption, Post Type, Week/Day.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* File Info */}
          <div className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-xl" data-testid="selected-file">
            <div className="flex items-center gap-3">
              {isExcel ? (
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
              ) : (
                <FileText className="w-8 h-8 text-[#2563eb]" />
              )}
              <div>
                <p className="font-medium text-[#1e293b]">{selectedFile.name}</p>
                <p className="text-xs text-[#94a3b8]">{(selectedFile.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clearFile} className="text-[#94a3b8] hover:text-[#1e293b]" data-testid="clear-file-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Preview loading */}
          {isPreviewing && (
            <div className="flex items-center gap-2 p-3 text-sm text-[#64748b]">
              <div className="w-4 h-4 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
              Analyzing file...
            </div>
          )}

          {/* Preview Results */}
          {preview && (
            <div className="space-y-4">
              {/* Column Mapping */}
              <div className="p-4 bg-[#f0fdf4] border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">
                    {preview.total_rows} posts detected
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(preview.columns_detected).map(([field, originalCol]) => (
                    <span key={field} className="text-xs px-2.5 py-1 rounded-full bg-white border border-green-200 text-green-700">
                      {originalCol} → <span className="font-bold">{field}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Week Start Date Picker (for relative dates) */}
              {preview.uses_relative_dates && (
                <div className="p-4 bg-[#eff6ff] border border-blue-200 rounded-xl" data-testid="week-start-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#2563eb]" />
                    <span className="text-sm font-semibold text-[#1e3a5f]">
                      When does Week 1 start?
                    </span>
                  </div>
                  <p className="text-xs text-[#64748b] mb-3">
                    This file uses relative dates (Week/Day). Pick the Monday that Week 1 begins.
                  </p>
                  <Input
                    type="date"
                    value={weekStartDate}
                    onChange={(e) => setWeekStartDate(e.target.value)}
                    className="max-w-[220px] border-blue-200 focus:border-[#2563eb]"
                    data-testid="week-start-date-input"
                  />
                </div>
              )}

              {/* Preview Rows */}
              {preview.preview?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2">Preview (first 3 rows)</p>
                  <div className="space-y-2">
                    {preview.preview.map((row, i) => (
                      <div key={i} className="p-3 bg-[#f8fafc] rounded-lg border border-[#e2e8f0] text-sm">
                        <span className="font-semibold text-[#1e293b]">{row.title || row.theme || 'Untitled'}</span>
                        {row.post_type && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">{row.post_type}</span>
                        )}
                        {row.content && (
                          <p className="text-xs text-[#64748b] mt-1 line-clamp-1">{row.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import Button */}
              <Button
                onClick={handleUpload}
                disabled={isUploading || (preview.uses_relative_dates && !weekStartDate)}
                className="btn-primary rounded-xl w-full"
                data-testid="import-btn"
              >
                {isUploading ? 'Importing...' : `Import ${preview.total_rows} Posts`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
